import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { getOperator, type Operator } from '../lib/auth';
import { fetchMe, logout as apiLogout } from '../lib/api';

type AuthContextValue = {
  operator: Operator | null;
  setOperator: (operator: Operator | null) => void;
  refreshOperator: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [operator, setOperator] = useState<Operator | null>(() => getOperator());

  const refreshOperator = useCallback(async () => {
    const me = await fetchMe();
    setOperator(me);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setOperator(null);
  }, []);

  const value = useMemo(
    () => ({ operator, setOperator, refreshOperator, logout }),
    [operator, refreshOperator, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
